import Vue from 'vue';
import Vuex, { Commit } from 'vuex';

Vue.use(Vuex);

declare let YT: any;
declare let window: any;
declare let google: any;

const googleMapsAPIKey = document.querySelector('.js-l-page')?.getAttribute('data-google-apikey');
const googleMapsClient = document.querySelector('.js-l-page')?.getAttribute('data-google-clientid');
let googleMapsAPILoading = false;

/**
 * Default variables for youtube player, if no other are given.
 * API documentation of the parameters see https://developers.google.com/youtube/player_parameters
 */
const DEFAULT_PLAYER_VARS = {
	rel: 0,
	color: 'white',
	controls: 2,
	autoplay: 0,
	start: 0,
	end: 0,
	fs: 1,
};

type YoutubeVideo = {
	videoId: string;
	playerVars: object;
	player?: object;
};

type YoutubeState = {
	videos: YoutubeVideo[];
	youtubeAPIloading: boolean;
	youtubeAPIloaded: boolean;
};

type GoogleMapsState = {
	googleMapsAPIloading: boolean;
	googleMapsAPIloaded: boolean;
};

type GoogleState = {
	youtube: YoutubeState;
	googleMaps: GoogleMapsState;
};

/**
 * Insert iframe API script into document.
 * API documentation see https://developers.google.com/youtube/iframe_api_reference.
 */
function insertIframeAPIScript() {
	const tag = document.createElement('script');
	tag.src = 'https://www.youtube.com/iframe_api';
	const firstScriptTag = document.getElementsByTagName('script')[0];
	firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
}

/**
 * As soon as the player of a video is ready:
 * make it accessible, so e.g. the video can be stopped via code.
 */
function onPlayerReady(commit: Commit, state: GoogleState, video: YoutubeVideo, player: any) {
	const videos = [...state.youtube.videos];
	videos.find((oldVideo, index) => {
		if (oldVideo.videoId === video.videoId) {
			videos[index].player = player.target;
		}
	});
	const youtube = {
		...state.youtube,
		videos,
	};
	commit('SET_YT_VIDEOS', {
		...state,
		youtube,
	});
}

async function insertGoogleMapsAPIScript(apiKey: string, clientId: string) {
	const lang = document.querySelector('html')?.lang;
	const markerCluster = document.createElement('script');
	markerCluster.src = `/assets/js/google.markerclusterer.js`;
	document.body.append(markerCluster);
	markerCluster.onload = () => {
		const gmap = document.createElement('script');
		gmap.src = `https://maps.googleapis.com/maps/api/js?v=3&callback=googleMapsAPILoaded&libraries=places&language=${lang}&region=${lang}&client=${clientId}&key=${apiKey}`;
		window.googleMapsAPILoaded = () => {
			const event = new Event('googleMapsAPILoaded');
			window.dispatchEvent(event);
		};
		document.body.append(gmap);
	};
}

/**
 * Store to handle all matters concerning any Google APIs.
 * * Youtube
 */
export const googleStore = {
	namespaced: true,
	state: {
		youtube: {
			videos: [],
			youtubeAPIloading: false,
			youtubeAPIloaded: false,
		},
		googleMaps: {
			googleMapsAPIloading: false,
			googleMapsAPIloaded: false,
		},
	},
	mutations: {
		SET_YT_VIDEOS(state: GoogleState, payload: GoogleState) {
			state.youtube.videos = payload.youtube.videos;
		},
		RESET_YT_VIDEOS(state: GoogleState) {
			state.youtube.videos = [];
		},
		SET_YT_API_LOADING(state: GoogleState, payload: GoogleState) {
			state.youtube.youtubeAPIloading = payload.youtube.youtubeAPIloading;
		},
		SET_YT_API_LOADED(state: GoogleState, payload: GoogleState) {
			state.youtube.youtubeAPIloaded = payload.youtube.youtubeAPIloaded;
		},
		SET_GMAP_API_LOADING(state: GoogleState, payload: GoogleState) {
			state.googleMaps.googleMapsAPIloading = payload.googleMaps.googleMapsAPIloading;
		},
		SET_GMAP_API_LOADED(state: GoogleState, payload: GoogleState) {
			state.googleMaps.googleMapsAPIloaded = payload.googleMaps.googleMapsAPIloaded;
		},
	},
	actions: {
		loadVideo({ commit, state }: { commit: Commit; state: GoogleState }, videoId: string) {
			if (state.youtube.videos && state.youtube.videos.length && typeof YT !== 'undefined') {
				state.youtube.videos.find((video) => {
					if (video.videoId === videoId) {
						new YT.Player(video.videoId, {
							videoId: video.videoId,
							playerVars: video.playerVars,
							events: {
								onReady: (newPlayer: object) => onPlayerReady(commit, state, video, newPlayer),
							},
						});
					}
				});
			}
		},
		addVideo({ commit, state }: { commit: Commit; state: GoogleState }, inputVideo: YoutubeVideo) {
			const videos = [...state.youtube.videos];
			// if the video is not in the store, add it
			const isVideoAlreadyInStore = videos.find((video) => video.videoId === inputVideo.videoId);
			if (!isVideoAlreadyInStore) {
				const video = {
					videoId: inputVideo.videoId,
					playerVars: {
						...DEFAULT_PLAYER_VARS,
						...inputVideo.playerVars,
					},
				};
				videos.push(video);
			} else {
				// if the video is already in the store, update the playerVars for that video
				// in case those playerVars changed otherwise Vuex might persist the old playerVars
				const videoIndex = videos.findIndex((video) => video.videoId === inputVideo.videoId);
				videos[videoIndex].playerVars = {
					...DEFAULT_PLAYER_VARS,
					...inputVideo.playerVars,
				};
			}
			const youtube = {
				...state.youtube,
				videos,
			};
			commit('SET_YT_VIDEOS', {
				...state,
				youtube,
			});
		},
		loadYoutubeAPI({ commit, state }: { commit: Commit; state: GoogleState }) {
			if ((!state.youtube.youtubeAPIloaded && !state.youtube.youtubeAPIloading) || typeof YT === 'undefined') {
				const youtube = {
					...state.youtube,
					youtubeAPIloading: true,
				};
				commit('SET_YT_API_LOADING', {
					...state,
					youtube,
				});

				// prepare window method for when iframe API has loaded
				// (is called automatically)
				window.onYouTubeIframeAPIReady = () => {
					const youtube = {
						...state.youtube,
						youtubeAPIloading: false,
						youtubeAPIloaded: true,
					};
					commit('SET_YT_API_LOADING', {
						...state,
						youtube,
					});
					commit('SET_YT_API_LOADED', {
						...state,
						youtube,
					});
				};
				insertIframeAPIScript();
			}
		},
		loadGoogleMapsAPI(
			{ commit, state }: { commit: Commit; state: GoogleState },
			{ apiKey, clientId }: { apiKey: string; clientId: string },
		) {
			if (
				((!state.googleMaps.googleMapsAPIloaded && !state.googleMaps.googleMapsAPIloading) ||
					typeof google === 'undefined') &&
				!googleMapsAPILoading
			) {
				googleMapsAPILoading = true;
				const googleMaps = {
					...state.googleMaps,
					googleMapsAPIloading: true,
				};
				commit('SET_GMAP_API_LOADING', {
					...state,
					googleMaps,
				});
				window.addEventListener('googleMapsAPILoaded', () => {
					const googleMaps = {
						...state.googleMaps,
						googleMapsAPIloading: false,
						googleMapsAPIloaded: true,
					};
					commit('SET_GMAP_API_LOADING', {
						...state,
						googleMaps,
					});
					commit('SET_GMAP_API_LOADED', {
						...state,
						googleMaps,
					});
				});
				insertGoogleMapsAPIScript(apiKey, clientId);
			} else {
				const event = new Event('googleMapsAPILoaded');
				window.dispatchEvent(event);
			}
		},
	},
};
